import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helpers ---------------------------------------------------------------
const blankItem = () => ({
  id: undefined,
  image: "",
  caption: { en: "", fr: "", es: "" },
  rotation: 0,
});

// Admin page for the homepage "Our Heritage" Polaroid wall.
// Mirrors the layout pattern used by AdminTestimonialsPage:
//   - Cookie-based admin session (shared with the rest of /admin)
//   - List of editable cards: image URL + 3-language caption + rotation
//   - Add / Move up / Move down / Delete per row
//   - Save (PUT) / Reset to defaults (DELETE)
const AdminHeritagePage = () => {
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    const res = await axios.get(`${API}/admin/heritage/polaroids`);
    setItems((res.data?.items || []).map((it) => ({ ...blankItem(), ...it, caption: { ...blankItem().caption, ...(it.caption || {}) } })));
  };

  useEffect(() => {
    // Verify existing cookie session on mount.
    let cancelled = false;
    axios
      .get(`${API}/admin/session/verify`)
      .then(() => fetchItems())
      .then(() => { if (!cancelled) setUnlocked(true); })
      .catch(() => { if (!cancelled) setUnlocked(false); });
    return () => { cancelled = true; };
  }, []);

  const tryUnlock = async (pwd) => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/session/login`, { password: pwd });
      await fetchItems();
      setUnlocked(true);
      setPassword(""); // cookie carries auth — clear from memory.
    } catch (e) {
      if (e?.response?.status === 401) toast.error("Wrong password");
      else if (e?.response?.status === 429) toast.error("Too many attempts — try again shortly");
      else toast.error("Unable to log in");
      setUnlocked(false);
    } finally {
      setLoading(false);
    }
  };

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const updateCaption = (idx, langKey, val) => {
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, caption: { ...it.caption, [langKey]: val } } : it))
    );
  };
  const move = (idx, dir) => {
    setItems((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const removeAt = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addNew = () => setItems((prev) => [...prev, blankItem()]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items
          .filter((it) => it.image && it.image.trim().length > 0)
          .map((it) => ({
            id: it.id,
            image: it.image.trim(),
            caption: { en: it.caption?.en || "", fr: it.caption?.fr || "", es: it.caption?.es || "" },
            rotation: Number(it.rotation) || 0,
          })),
      };
      await axios.put(`${API}/admin/heritage/polaroids`, payload);
      toast.success("Heritage Polaroids saved");
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset to default Heritage wall? Custom edits will be lost.")) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/admin/heritage/polaroids`);
      toast.success("Reset to defaults");
      await fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  // ─────────── PASSWORD GATE ──────────────────────────────────────────
  if (!unlocked) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-[#FDFBF7] px-6 py-20">
        <form
          onSubmit={(e) => { e.preventDefault(); void tryUnlock(password); }}
          className="w-full max-w-md bg-[#FDFBF7] border border-[#2C2621]/15 p-8"
          data-testid="admin-heritage-gate"
        >
          <Lock className="w-5 h-5 text-[#C16542]" strokeWidth={1.6} />
          <h1 className="font-serif-x mt-3 text-3xl text-[#2C2621]">Heritage Polaroids</h1>
          <p className="mt-1 text-sm text-[#5C5248]">Manage the homepage memory wall.</p>
          <label className="block overline mt-6 mb-2">Admin password</label>
          <input
            autoFocus
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent outline-none border border-[#2C2621]/20 focus:border-[#C16542] px-4 py-3 text-[#2C2621]"
            data-testid="admin-heritage-password-input"
          />
          <button
            type="submit"
            disabled={loading || !password}
            className="mt-6 inline-flex items-center gap-2 bg-[#C16542] hover:bg-[#A35133] disabled:opacity-60 text-[#FDFBF7] px-7 py-3 text-xs tracking-[0.22em] uppercase"
            data-testid="admin-heritage-password-submit"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
            Unlock
          </button>
        </form>
      </div>
    );
  }

  // ─────────── EDITOR ─────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#2C2621] px-6 md:px-10 py-12" data-testid="admin-heritage-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <p className="overline">Homepage CMS</p>
            <h1 className="font-serif-x text-4xl md:text-5xl tracking-tight">Heritage Polaroids</h1>
            <p className="mt-2 text-sm text-[#5C5248] max-w-2xl">
              The memory wall rendered inside the &quot;Our Heritage&quot; section of the homepage. Add archive
              photos, family memories or signature moments — each as a slightly rotated card with a
              short caption (date, place, people).
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={resetDefaults}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] transition-colors disabled:opacity-60"
              data-testid="admin-heritage-reset"
            >
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} />
              Reset
            </button>
            <button
              type="button"
              onClick={addNew}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase border border-[#2C2621]/20 hover:border-[#C16542] hover:text-[#C16542] transition-colors"
              data-testid="admin-heritage-add"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.6} />
              Add card
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] disabled:opacity-60"
              data-testid="admin-heritage-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save changes
            </button>
          </div>
        </div>

        <ul className="space-y-6">
          {items.length === 0 && (
            <li className="border border-dashed border-[#2C2621]/20 p-10 text-center text-sm text-[#5C5248]">
              No polaroids yet — click <strong>Add card</strong> to start the wall.
            </li>
          )}
          {items.map((it, idx) => (
            <li
              key={it.id || `new-${idx}`}
              className="grid md:grid-cols-12 gap-5 p-5 border border-[#2C2621]/12 bg-[#FDFBF7]"
              data-testid={`admin-heritage-item-${idx}`}
            >
              {/* Preview */}
              <div className="md:col-span-3">
                <div className="aspect-[4/5] bg-[#FAF6EE] border border-[#2C2621]/10 overflow-hidden">
                  {it.image ? (
                    <img src={it.image} alt={`Polaroid ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" decoding="async"/>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/60">
                      No image
                    </div>
                  )}
                </div>
                <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-[#5C5248]">Rotation</p>
                <input
                  type="range"
                  min={-12}
                  max={12}
                  step={0.5}
                  value={it.rotation || 0}
                  onChange={(e) => updateItem(idx, { rotation: Number(e.target.value) })}
                  className="w-full mt-1"
                  data-testid={`admin-heritage-item-${idx}-rotation`}
                />
                <p className="text-[10px] text-[#5C5248]/80 tabular-nums">{(it.rotation || 0).toFixed(1)}°</p>
              </div>

              {/* Inputs */}
              <div className="md:col-span-9 space-y-3">
                <div>
                  <label className="block overline mb-1">Image URL</label>
                  <input
                    type="url"
                    value={it.image || ""}
                    onChange={(e) => updateItem(idx, { image: e.target.value })}
                    placeholder="https://..."
                    className="w-full bg-transparent border border-[#2C2621]/15 focus:border-[#C16542] outline-none px-3 py-2 text-sm"
                    data-testid={`admin-heritage-item-${idx}-image`}
                  />
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {["en", "fr", "es"].map((lang) => (
                    <div key={lang}>
                      <label className="block overline mb-1">Caption · {lang.toUpperCase()}</label>
                      <input
                        type="text"
                        value={it.caption?.[lang] || ""}
                        onChange={(e) => updateCaption(idx, lang, e.target.value)}
                        placeholder="Date · place · people"
                        className="w-full bg-transparent border border-[#2C2621]/15 focus:border-[#C16542] outline-none px-3 py-2 text-sm"
                        data-testid={`admin-heritage-item-${idx}-caption-${lang}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => move(idx, -1)}
                    disabled={idx === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.2em] uppercase border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40"
                    data-testid={`admin-heritage-item-${idx}-up`}
                  >
                    <ArrowUp className="w-3 h-3" /> Up
                  </button>
                  <button
                    type="button"
                    onClick={() => move(idx, 1)}
                    disabled={idx === items.length - 1}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.2em] uppercase border border-[#2C2621]/15 hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-40"
                    data-testid={`admin-heritage-item-${idx}-down`}
                  >
                    <ArrowDown className="w-3 h-3" /> Down
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAt(idx)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] tracking-[0.2em] uppercase border border-[#C16542]/35 text-[#C16542] hover:bg-[#C16542] hover:text-[#FDFBF7] transition-colors"
                    data-testid={`admin-heritage-item-${idx}-remove`}
                  >
                    <Trash2 className="w-3 h-3" /> Remove
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AdminHeritagePage;
